import { useCallback, useEffect, useRef, useState } from "react";
import { TbPlayerPlayFilled, TbX } from "react-icons/tb";
import useScrollLock from "../../hooks/useScrollLock";
import styles from "./VideoModal.module.css";

// Mesmo pôster da VideoSection — servido de public/ (~107 KB), fora do bundle.
const POSTER_SRC = `${process.env.PUBLIC_URL}/video-apresentacao-poster.jpg`;

/**
 * VideoModal — modal com embed do YouTube em carregamento preguiçoso (facade).
 *
 * Enquanto o usuário não clica no play, NENHUM <iframe> existe no DOM: só o
 * pôster. Isso evita que abrir o modal puxe ~1 MB de scripts do YouTube e
 * garante que nada toque com som sem ação explícita do usuário.
 *
 * @param {boolean} open - Controla a visibilidade.
 * @param {string} youtubeId - ID do vídeo no YouTube.
 * @param {Function} onClose - Callback de fechamento (Esc, overlay ou botão ✕).
 */
const VideoModal = ({ open, youtubeId, onClose }) => {
  const [play, setPlay] = useState(false);
  const closeBtnRef = useRef(null);

  useScrollLock(open);

  // Fecha o facade sempre que o modal fecha — reabrir nunca cai direto no iframe.
  useEffect(() => {
    if (!open) setPlay(false);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    // Guarda quem abriu o modal para devolver o foco ao fechar.
    // (A trava de rolagem do <body> mora em `useScrollLock`, acima.)
    const opener = document.activeElement;

    // Move o foco para dentro do modal.
    closeBtnRef.current?.focus();

    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      if (opener instanceof HTMLElement) opener.focus();
    };
  }, [open, onClose]);

  const handleOverlayClick = useCallback(() => onClose(), [onClose]);
  const stopPropagation = useCallback((e) => e.stopPropagation(), []);

  if (!open) return null;

  return (
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-label="Vídeo de apresentação"
      onClick={handleOverlayClick}
    >
      <div className={styles.box} onClick={stopPropagation}>
        <button
          type="button"
          ref={closeBtnRef}
          className={styles.close}
          onClick={onClose}
          aria-label="Fechar vídeo"
        >
          <TbX aria-hidden="true" />
        </button>

        {play ? (
          <iframe
            className={styles.frame}
            src={`https://www.youtube-nocookie.com/embed/${youtubeId}?autoplay=1&rel=0&modestbranding=1`}
            title="Apresentação Nexus Tech"
            allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
            allowFullScreen
          />
        ) : (
          <button
            type="button"
            className={styles.facade}
            onClick={() => setPlay(true)}
            aria-label="Reproduzir vídeo"
          >
            <img
              className={styles.poster}
              src={POSTER_SRC}
              alt=""
              width="1280"
              height="720"
              decoding="async"
            />
            <span className={styles.playBadge} aria-hidden="true">
              <TbPlayerPlayFilled className={styles.playIcon} />
            </span>
          </button>
        )}
      </div>
    </div>
  );
};

export default VideoModal;
